import type { MouseEvent } from 'react';

import Icon from '@components/atoms/icon';
import { useCustomTransfersStore } from '@lib/transfers/store/useCustomTransfersStore';
import { useRecommendedTransfersStore } from '@lib/transfers/store/useRecommendedTransfersStore';
import type { TransfersCardMode } from '@lib/transfers/utils/transfers-view-state';
import type { TransferRecommendation } from '@api-service/recommendations';

type TransferPlayer = TransferRecommendation['in'];

type PlayerActions = {
  restorePlayer: (playerId: string) => void;
  dismissPlayer: (player: TransferPlayer, transferType: 'in' | 'out') => void;
};

const useRecommendedPlayerActions = (): PlayerActions => {
  const disablePlayer = useRecommendedTransfersStore(
    (state) => state.disablePlayer
  );
  const restorePlayer = useRecommendedTransfersStore(
    (state) => state.restorePlayer
  );
  const addPendingPlayerIn = useRecommendedTransfersStore(
    (state) => state.addPendingPlayerIn
  );
  const addPendingBlacklistedIn = useRecommendedTransfersStore(
    (state) => state.addPendingBlacklistedIn
  );

  return {
    restorePlayer,
    dismissPlayer: (player, transferType) => {
      disablePlayer(player.id);
      if (transferType === 'in') {
        addPendingBlacklistedIn({
          id: player.id,
          displayName: player.displayName,
          team: player.team.shortName,
          type: 'in'
        });
      } else {
        addPendingPlayerIn(player.id);
      }
    }
  };
};

const useCustomPlayerActions = (): PlayerActions => {
  const disablePlayer = useCustomTransfersStore((state) => state.disablePlayer);
  const restorePlayer = useCustomTransfersStore((state) => state.restorePlayer);
  const addPendingExcludePlayer = useCustomTransfersStore(
    (state) => state.addPendingExcludePlayer
  );
  const addPendingPlayerIn = useCustomTransfersStore(
    (state) => state.addPendingPlayerIn
  );

  return {
    restorePlayer,
    dismissPlayer: (player, transferType) => {
      disablePlayer(player.id);
      if (transferType === 'in') {
        addPendingExcludePlayer(player.id);
      } else {
        addPendingPlayerIn({
          value: player.id,
          label: player.displayName,
          team: player.team.shortName
        });
      }
    }
  };
};

type ActionChipProps = {
  player: TransferPlayer;
  transferType: 'in' | 'out';
  isDismissed: boolean;
  actions: PlayerActions;
  className?: string;
};

const ActionChip = ({
  player,
  transferType,
  isDismissed,
  actions,
  className = ''
}: ActionChipProps) => {
  const visibleLabel = isDismissed
    ? 'Restore'
    : transferType === 'in'
    ? "Don't recommend"
    : "Don't sell";

  const accessibleLabel = `${visibleLabel} ${player.displayName}`;

  const dataCy = isDismissed
    ? 'player-action--restore'
    : transferType === 'in'
    ? 'player-action--dont-recommend'
    : 'player-action--dont-sell';

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // The row itself selects the transfer for comparison; keep this action
    // from doubling as a row click.
    event.stopPropagation();
    if (isDismissed) {
      actions.restorePlayer(player.id);
    } else {
      actions.dismissPlayer(player, transferType);
    }
  };

  const toneClass = isDismissed
    ? 'bg-primary-50 font-bold text-primary-700'
    : 'bg-neutral-100 font-semibold text-neutral-600 hover:bg-danger-50 hover:text-danger-600';

  return (
    <button
      type="button"
      onClick={handleClick}
      title={accessibleLabel}
      aria-label={accessibleLabel}
      data-cy={dataCy}
      data-cy-player-id={player.id}
      className={`inline-flex min-h-[32px] items-center gap-[5px] whitespace-nowrap rounded-md px-2 font-open-sans text-[11px] transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 md:min-h-0 md:h-[22px] ${toneClass} ${className}`}
    >
      <Icon name={isDismissed ? 'Cached' : 'Block'} fontSize="12px" />
      {visibleLabel}
    </button>
  );
};

type TransferPlayerActionProps = Omit<ActionChipProps, 'actions'> & {
  mode: TransfersCardMode;
};

const RecommendedPlayerAction = (props: Omit<ActionChipProps, 'actions'>) => {
  const actions = useRecommendedPlayerActions();
  return <ActionChip actions={actions} {...props} />;
};

const CustomPlayerAction = (props: Omit<ActionChipProps, 'actions'>) => {
  const actions = useCustomPlayerActions();
  return <ActionChip actions={actions} {...props} />;
};

/**
 * Labelled per-player opt-out chip on a transfer row: "Don't sell" on the
 * sell side, "Don't recommend" on the buy side, flipping to "Restore" once
 * dismissed. Mirrors the transfer player menu actions in scoreline-app.
 */
export const TransferPlayerAction = ({
  mode,
  ...props
}: TransferPlayerActionProps) => {
  if (mode === 'custom') {
    return <CustomPlayerAction {...props} />;
  }
  return <RecommendedPlayerAction {...props} />;
};
